import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";

/**
 * Compact user menu — replaces the always-visible "email + Sign out" pair
 * in the header. Click the avatar to reveal a dropdown with the user's
 * name + email, theme toggle, settings shortcut, and sign-out. Closes on
 * outside-click or Escape.
 *
 * This is where the Dark/Light toggle lives now (used to be in the
 * preview banner). The avatar gradient is deterministic per user id so
 * different teammates get visually distinct dots in shared screens.
 */
interface Props {
  /** When true, render compact (avatar only) — used in the workspace
   *  mode strip on the quote editor where horizontal real estate is
   *  scarce. Hover/click still shows the full menu. */
  compact?: boolean;
}

export function UserMenu({ compact = false }: Props) {
  const { user, signOut, setPasswordPrompt } = useAuthStore();
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside-click or Escape — standard dropdown UX.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // First two letters of the user's email for the avatar — uppercased.
  // Falls back to "··" if email is missing (rare, but keeps the avatar
  // from collapsing).
  const initials = (user?.email ?? "··").slice(0, 2).toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded border border-border px-2 py-1 hover:bg-bg2 transition"
        title={user?.email || "Account"}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          className="inline-flex items-center justify-center rounded-full text-[10px] font-bold text-white"
          style={{
            width: 22,
            height: 22,
            background: "linear-gradient(135deg, var(--neo-teal), var(--neo-purple))",
          }}
          aria-hidden="true"
        >
          {initials}
        </span>
        {!compact && (
          <span className="text-xs text-fg-muted max-w-[180px] truncate">
            {user?.email}
          </span>
        )}
        <span className="text-[9px] text-fg-dim opacity-60">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 z-50 min-w-[220px] rounded-lg border shadow-2xl"
          style={{
            background: "var(--neo-bg2)",
            borderColor: "var(--neo-border-strong, rgba(255,255,255,.14))",
            padding: 6,
            boxShadow: "0 12px 40px rgba(0,0,0,.5)",
          }}
        >
          <div className="px-2.5 py-2">
            <div className="text-xs font-semibold text-fg truncate">
              {user?.email ?? "Signed in"}
            </div>
            <div className="text-[10px] text-fg-dim mt-0.5">
              Account menu
            </div>
          </div>
          <div className="h-px my-1.5 bg-border" />
          <button
            type="button"
            onClick={() => {
              toggleTheme();
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded text-xs text-fg-muted hover:bg-bg1 hover:text-fg transition text-left"
            role="menuitem"
          >
            <span className="opacity-70">{theme === "dark" ? "🌑" : "🌞"}</span>
            <span className="flex-1">
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </span>
          </button>
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-2.5 py-2 rounded text-xs text-fg-muted hover:bg-bg1 hover:text-fg transition"
            role="menuitem"
          >
            <span className="opacity-70">⚙</span>
            <span>Settings</span>
          </Link>
          <Link
            to="/catalogue"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-2.5 py-2 rounded text-xs text-fg-muted hover:bg-bg1 hover:text-fg transition"
            role="menuitem"
          >
            <span className="opacity-70">📚</span>
            <span>Catalogue</span>
          </Link>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setPasswordPrompt("change");
            }}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded text-xs text-fg-muted hover:bg-bg1 hover:text-fg transition text-left"
            role="menuitem"
          >
            <span className="opacity-70">🔑</span>
            <span>Change password</span>
          </button>
          <div className="h-px my-1.5 bg-border" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded text-xs hover:bg-bg1 transition text-left"
            style={{ color: "var(--neo-red)" }}
            role="menuitem"
          >
            <span className="opacity-70">↪</span>
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
